import React, { useEffect, useState } from 'react'
import clsx from 'clsx'
import { showErrorToast } from 'components/utility/toast'
import { openPackageModal } from 'lib/openPackageModal'
import { selectFlagsEverLoaded, useFlagStore } from 'stores/flagStore'
import { selectPackageEverLoaded, usePackageStore } from 'stores/packageStore'
import { getFinalizationStatus } from './api'
import { useFlagRequirementCounts } from './hooks/useFlagRequirementCounts'

// initialCounts is server-seeded (see Package::Plugins finalization_props) so the button
// renders in its settled state on first paint, before FlagLoader/PackageLoader's fetches
// resolve — without it, incompleteCount/completeCount/total default to 0/0/0, which makes
// countsMatchTotal vacuously true and the button flashes active before flipping to its
// real state (BTB-418).
const EMPTY_COUNTS = { incompleteCount: 0, completeCount: 0, total: 0 }

export default function FinalizationCTA({ packageId, labels, processorWorking, initialCounts = EMPTY_COUNTS }) {
  const [loading, setLoading] = useState(false)
  const [responseSent, setResponseSent] = useState(!processorWorking)
  const liveCounts = useFlagRequirementCounts()
  const packageEverLoaded = usePackageStore(selectPackageEverLoaded)
  const flagsEverLoaded = useFlagStore(selectFlagsEverLoaded)
  const dataSettled = packageEverLoaded && flagsEverLoaded
  const { incompleteCount, completeCount, total } = dataSettled ? liveCounts : initialCounts
  const countsMatchTotal = incompleteCount + completeCount === total

  useEffect(() => {
    if (!processorWorking) setResponseSent(true)
  }, [processorWorking])

  useEffect(() => {
    const handler = (e) => {
      if (e.detail.packageId === packageId) setResponseSent(true)
    }

    document.addEventListener('package:acknowledgement-response-sent', handler)
    return () => document.removeEventListener('package:acknowledgement-response-sent', handler)
  }, [packageId])

  const handleClick = async () => {
    if (loading || responseSent) return
    setLoading(true)
    try {
      const { data } = await getFinalizationStatus(packageId)
      if (!data?.eligible) {
        showErrorToast({ message: 'This package is not ready to be finalized yet.' })
        return
      }
      openPackageModal(packageId, 'send_acknowledgement').catch((error) => {
        console.error('[FinalizationCTA] openPackageModal error', error)
      })
    } catch (error) {
      console.error('[FinalizationCTA] getFinalizationStatus error', error)
      showErrorToast({ message: 'Something went wrong. Please try again.' })
    } finally {
      setLoading(false)
    }
  }

  const buttonDisabled = responseSent || loading || !countsMatchTotal
  const buttonClasses = clsx('dome-btn dome-btn-base', buttonDisabled ? 'dome-btn-disabled' : 'dome-btn-dark-green')

  return (
    <button className={buttonClasses} onClick={handleClick} disabled={buttonDisabled}>
      {loading ? 'Checking...' : labels.issuesSummaryButton}
    </button>
  )
}
